import { Api, getPaginatedVIPs, getVIPById } from "@api";
import { FilterType, Table } from "@components";
import { Cell, Row } from "@components/table/types";
import { Box, Button, Typography, IconButton } from "@material-ui/core";
import * as React from "react";
import { useState, useCallback } from "react";
import { M_TABLE_HEADERS_VIPS } from "../constants";
import CloseIcon from "@material-ui/icons/Close";
import { Form, Modal } from "@components";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import { VIPDetails } from "./detail";
import { useEffect } from "react";
import { MaintenanceBreadcrumbs } from "../breadcrumbs";
import { useVIPStore } from "../../../../../store/hooksy";
import { FormData } from "../vip/detail/form";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { VIP } from "@api/types/vips";
import { exportVIPApprovers } from "@api/excel-exporters/exportVIPApprovers";

const columns: Cell[] = [
  { data: M_TABLE_HEADERS_VIPS.VIP_EMAIL },
  { data: M_TABLE_HEADERS_VIPS.APPROVER_EMAIL },
  { data: M_TABLE_HEADERS_VIPS.IS_ACTIVE },
];

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

const formatBool = (status: boolean): string => {
  return status ? "Yes" : "No";
};

type DataPaginated = {
  vips: VIP[];
  totalCount: number;
};

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

export type SelectedItem = {
  selectedVIP: VIP;
};

type FormValues = {
  id: number;
  vipEmail: string;
  void: boolean;
  active: boolean;
  approverEmail: string;
};

type Key = keyof FormValues;

export const VIPs: React.FC = () => {
  const [vipStore, setVIPStore] = useVIPStore();

  const [vips, setVIPs] = useState<VIP[]>([]);
  const [dataList, setTableData] = useState<Row<VIP>[]>([]);
  const [isLoading, setIsloading] = useState<boolean>(true);
  const [open, setOpen] = React.useState<boolean>(false);
  const [openUpdate, setOpenUpdate] = React.useState<boolean>(false);
  const [action, setAction] = React.useState<string>("Add");
  const [where, setWhere] = React.useState<string>("");
  const [pending, setPending] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<VIP | undefined>(
    undefined
  );
  const [page, setPage] = React.useState<number>(1);
  // set to 0 to get all the records.
  const [perPage, setPerPage] = React.useState<number>(0);
  const [total, setTotal] = React.useState<number>(0);

  const updateData = () => {
    incrementCounter();
  };

  const onSearchUpdate = useCallback((term: string) => {
    setWhere(term);
  }, []);

  const handleClose = () => {
    setSelectedItem(undefined);
    setOpen(false);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const form = Form.useManager<FormValues>({
    id: 0,
    vipEmail: "",
    void: false,
    active: false,
    approverEmail: "",
  });

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "vipEmail":
      case "approverEmail":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value) || Form.Validators.isEmail(value),
        ]);

      case "active":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);

      default:
        return Promise.resolve([name as Key]);
    }
  }

  function handleAddButtonClick(): void {
    setPending(true);
    Api.vip
      .postVIP({
        ...form.values,
      })
      .then(() => {
        setPending(false);
        setIsOpenConfirmationModal(false);
        form.reset();
        handleClose();
        updateData();
      })
      .catch((error) => {
        setPending(false);
        console.log(error);
        // [TODO] Handle error
      });
  }

  function handleSubmitButtonClick(): void {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          setIsOpenConfirmationModal(true);
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  const showModal = () => {
    setOpen(true);
  };

  // get the updated vip by id
  useEffect(() => {
    const vipId = vipStore.vipId;
    if (vipId) {
      getVIPById(vipId)
        .then((response: any) => {
          let vipsCopy: any = dataList;
          let updatedVIP = response;
          let vipsNewCopy: any = [];
          vipsCopy.forEach((d: any) => {
            if (updatedVIP.id === d.id) {
              vipsNewCopy = [
                ...vipsNewCopy,
                {
                  id: updatedVIP.id,
                  vipEmail: updatedVIP.vipEmail,
                  void: updatedVIP.void,
                  active: updatedVIP.active,
                  approverEmail: updatedVIP.approverEmail,
                },
              ];
            } else {
              vipsNewCopy = [
                ...vipsNewCopy,
                {
                  id: d.id,
                  vipEmail: d.vipEmail,
                  void: d.void,
                  active: d.active,
                  approverEmail: d.approverEmail,
                },
              ];
            }
          });

          setTableData(vipsNewCopy);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [vipStore]);

  const [counter, updateCounter] = React.useState(0);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = React.useState<
    boolean
  >(false);

  useEffect(() => {
    getPaginatedVIPs(page, perPage, where).then((response: DataPaginated) => {
      console.log(response.vips);
      setVIPs(response.vips);
      setTotal(response.totalCount);
      setIsloading(false);
    });
  }, [counter, page, where]);

  const incrementCounter = () => {
    updateCounter(counter + 1);
  };

  const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });

  let editModal = null;
  if (!!selectedItem) {
    editModal = (
      <VIPDetails
        id={0}
        data={selectedItem}
        isOpen={openUpdate}
        onClickAdd={updateData}
        onClickClose={handleClose}
        action={action}
      />
    );
  }

  function handleExport() {
    exportVIPApprovers();
  }

  const pageName = "VIP Approvers";
  console.log(vips);
  return (
    <Box mt={2}>
      {editModal}
      <Modal
        isOpen={isOpenConfirmationModal}
        positionButton="center"
        actions={[
          {
            label: "Cancel",
            variant: "outlined",
            handler: () => {
              setIsOpenConfirmationModal(false);
            },
          },
          {
            label: pending ? "Submitting..." : "Submit",
            handler: () => {
              handleAddButtonClick();
            },
            disabled: pending,
          },
        ]}
      >
        <Typography align="center" paragraph>
          Are you sure you want to add this VIP Approver?
        </Typography>
      </Modal>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        fullWidth={true}
        maxWidth={"sm"}
      >
        <DialogTitle id="form-dialog-title" onClose={handleClose}>
          Add New VIP
        </DialogTitle>
        <DialogContent>
          <FormData data={undefined} onHandleChange={onChange} form={form} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmitButtonClick} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <MaintenanceBreadcrumbs title={pageName} />
      <Button style={{ float: "right" }} onClick={handleExport}>
        EXPORT TO EXCEL
      </Button>
      <Table
        data={vips.map((vip: VIP) => ({
          cells: [
            {
              data: vip.vipEmail,
              color: "blue",
              cursor: "pointer",
              onClick: () => {
                setSelectedItem(vip);
                setAction("Edit");
                setOpenUpdate(true);
              },
            },
            {
              data: vip.approverEmail,
              color: "blue",
              cursor: "pointer",
              onClick: () => {
                setSelectedItem(vip);
                setAction("Edit");
                setOpenUpdate(true);
              },
            },
            { data: formatBool(vip.active) },
          ],
          data: vip,
        }))}
        tableName={pageName}
        searchPlaceholder="Search by Name or Email"
        searchColumns={[
          M_TABLE_HEADERS_VIPS.VIP_EMAIL,
          M_TABLE_HEADERS_VIPS.APPROVER_EMAIL,
        ]}
        headers={columns}
        filters={[FilterType.SORT, FilterType.SEARCH, FilterType.PAGINATOR]}
        toolbar="M_VIP_TOOLBAR"
        onClickAdd={updateData}
        loading={isLoading}
      />
    </Box>
  );
};
